import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { viewpointGroupingRulesOperations } from './viewpointGroupingRulesOperations';
import {
  createGroupingRule,
  deleteGroupingRule,
  reorderGroupingRules,
  updateGroupingRule,
} from 'perspective/perspectiveEditor/perspectivesEditor$/actions';
import { Observable, combineLatest } from 'rxjs';
import { GroupingRulesState } from './groupingRulesTypes';
import {
  setGroupingOptions,
  setGroupingRules,
} from './viewpointGroupingRulesActions';
import { map } from 'rxjs/operators';
import { getInvalidGroupingRuleSelectOptionsByRuleId } from '../../perspectiveSidebar/perspectiveEditor/getInvalidGroupingRuleSelectOptionsByRuleId';
import { getGroupingRuleLabelsByRuleId } from 'streams/grouping/getGroupingRuleLabelsByRuleId';
import { groupingRuleLabelData$ } from 'streams/grouping/groupingRuleLabelData$';
import { PerspectiveGroupingRule } from '@ardoq/perspectives';
import { Features, featureOperations } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { disabledOptionProps } from 'viewpointBuilder/components/disabledOptionProps';
import {
  getRestrictedGroupFormattingWarningProps,
  isCreatingGroupingRuleDisabled,
  createGetGroupDisabledProps,
} from 'perspectiveSidebar/perspectiveEditor/groupUtils';
import { SelectedViewState } from '../selectedView/types';

export const createViewpointGroupingRules$ = (
  namespace: string,
  selectedView$: Observable<SelectedViewState>
): Observable<GroupingRulesState> =>
  combineLatest([
    reducedStream<GroupingRulesState>(
      `viewpointBuilder2.0/viewpointGroupingRules$:${namespace}`,
      viewpointGroupingRulesOperations.getInitialState(),
      [
        streamReducer(
          selectedView$,
          viewpointGroupingRulesOperations.setViewName
        ),
        reducer(
          createGroupingRule,
          viewpointGroupingRulesOperations.createGroupingRule
        ),
        reducer(
          updateGroupingRule,
          viewpointGroupingRulesOperations.updateGroupingRule
        ),
        reducer(
          reorderGroupingRules,
          viewpointGroupingRulesOperations.reorderGroupingRules
        ),
        reducer(
          deleteGroupingRule,
          viewpointGroupingRulesOperations.deleteGroupingRule
        ),
        reducer(
          setGroupingOptions,
          viewpointGroupingRulesOperations.setGroupingOptions
        ),
        reducer(
          setGroupingRules,
          viewpointGroupingRulesOperations.setGroupingRules
        ),
      ],
      { namespace }
    ),
    featureContext$,
    groupingRuleLabelData$,
  ]).pipe(
    map(([state, featureContext, groupingRuleLabelData]) => {
      const groupingRuleLabelsByRuleId = getGroupingRuleLabelsByRuleId(
        state.groupingRules,
        groupingRuleLabelData
      );

      const invalidGroupingSelectOptionsDataByRuleId =
        getInvalidGroupingRuleSelectOptionsByRuleId(
          state.groupingRules,
          state.groupingOptions,
          groupingRuleLabelsByRuleId
        );

      const getInvalidGroupingSelectOptions = (rule: PerspectiveGroupingRule) =>
        invalidGroupingSelectOptionsDataByRuleId[rule.id] || {};

      const restrictedGroupFormattingWarningProps =
        getRestrictedGroupFormattingWarningProps(
          state.viewName,
          state.groupingRules
        );
      const disableAddPerspectiveGroupButton = isCreatingGroupingRuleDisabled(
        state.viewName
      );
      const getGroupDisabledProps = createGetGroupDisabledProps(state.viewName);

      return {
        ...state,
        getInvalidGroupingSelectOptions,
        disabledOptionProps,
        restrictedGroupFormattingWarningProps,
        disableAddPerspectiveGroupButton,
        getGroupDisabledProps,
        isGroupingByTypeNamesEnabled: featureOperations.hasFeature(
          featureContext,
          Features.GROUPING_BY_TYPE_NAMES
        ),
      };
    })
  );
